// Libs
import classNames from 'classnames/bind';
import { useSelector } from 'react-redux';
import { Box, Stack, Tab } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useCallback, useEffect, useState } from 'react';
// Components, Layouts, Pages
import { BaseTabList, BaseToolbar } from '~/components/common';
import { UserChangePasswordTab, UserInfoTab } from '~/components/module/user';
// Others
import { IBaseTabItem } from '~/utils/interface/common';
import { RoleEnum, UserTabEnum } from '~/utils/enum/common';
import { StorageEnum } from '~/utils/enum/base';
import { EMPTY_STRING } from '~/utils/constants/common';
import { adminRouteAbsolute } from '~/utils/routes/roleRoutes/admin';
import { salesAdminRouteAbsolute } from '~/utils/routes/roleRoutes/salesAdmin';
import { salesPersonRouteAbsolute } from '~/utils/routes/roleRoutes/salesPerson';
import { operationsManagerRouteAbsolute } from '~/utils/routes/roleRoutes/operationsManager';
import { warehouseLeadRouteAbsolute } from '~/utils/routes/roleRoutes/warehouseLead';
import { warehouseStaffRouteAbsolute } from '~/utils/routes/roleRoutes/warehouseStaff';
import { foremanRouteAbsolute } from '~/utils/routes/roleRoutes/foreman';
import { crewRouteAbsolute } from '~/utils/routes/roleRoutes/crew';
import { getFullName } from '~/utils/helper/common';
import { selectDetailUser } from '~/thunks/user/userSlice';
import { loaderRouteAbsolute } from '~/utils/routes/roleRoutes/loader';
// Styles, images, icons
import styles from './UserDetail.module.scss';

type Props = {};

const cx = classNames.bind(styles);

const UserDetail = (props: Props) => {
  //#region Destructuring Props
  const {} = props;
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['admin/users', 'common/base']);
  const { hash } = useLocation();
  const navigate = useNavigate();
  const { id: userId } = useParams();
  const [searchParams] = useSearchParams();
  const role = searchParams.get('role');
  const currentRole: string = JSON.parse(localStorage.getItem(StorageEnum.ROLE)!)[0] ?? EMPTY_STRING;
  const accountId: string = localStorage.getItem(StorageEnum.ACCOUNT_ID) ?? EMPTY_STRING;

  const getMenuItems = useCallback(() => {
    const items: Array<IBaseTabItem> = [{ label: UserTabEnum.INFORMATION, component: <UserInfoTab /> }];

    if (userId === accountId || (currentRole === RoleEnum.SUPER_ADMIN && role !== RoleEnum.SUPER_ADMIN)) {
      items.push({ label: UserTabEnum.CHANGE_PASSWORD, component: <UserChangePasswordTab /> });
    }

    setTabItems(items);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  //#endregion Declare Hook

  //#region Selector
  const userDetail = useSelector(selectDetailUser);
  //#endregion Selector

  //#region Declare State
  const [tabKey, setTabKey] = useState<string>(UserTabEnum.INFORMATION);
  const [tabItems, setTabItems] = useState<IBaseTabItem[]>([]);
  //#endregion Declare State

  //#region Declare Memo
  //#endregion Declare Memo

  //#region Implement Hook
  useEffect(() => {
    if (hash) setTabKey(hash.slice(1));
  }, [hash]);

  useEffect(() => {
    getMenuItems();
  }, [getMenuItems]);
  //#endregion Implement Hook

  //#region Handle Function
  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    navigate(`#${newValue}`);
  };

  const renderTabContent = () => {
    const item = tabItems.find((tabItem: IBaseTabItem) => {
      return tabKey === tabItem.label;
    });

    return item?.component;
  };

  const handleOnBack = () => {
    switch (currentRole) {
      case RoleEnum.SUPER_ADMIN:
        navigate(adminRouteAbsolute.users);
        break;
      case RoleEnum.SALES_ADMIN:
        navigate(salesAdminRouteAbsolute.users);
        break;
      case RoleEnum.SALES_PERSON:
        navigate(salesPersonRouteAbsolute.users);
        break;
      case RoleEnum.OPERATIONS_MANAGER:
        navigate(operationsManagerRouteAbsolute.users);
        break;
      case RoleEnum.WAREHOUSE_LEAD:
        navigate(warehouseLeadRouteAbsolute.users);
        break;
      case RoleEnum.WAREHOUSE_STAFF:
        navigate(warehouseStaffRouteAbsolute.users);
        break;
      case RoleEnum.FOREMAN:
        navigate(foremanRouteAbsolute.users);
        break;
      case RoleEnum.CREW:
        navigate(crewRouteAbsolute.users);
        break;
      case RoleEnum.LOADER:
        navigate(loaderRouteAbsolute.users);
        break;
    }
  };
  //#endregion Handle Function

  return (
    <Box id='userDetailPage' className={cx('container')}>
      <BaseToolbar
        title={getFullName({ firstName: userDetail.firstName, lastName: userDetail.lastName })}
        isBack
        onBackToPreviousPage={handleOnBack}
      />

      <Stack height={5} />

      <BaseTabList value={tabKey} onChange={handleTabChange} type='small'>
        {tabItems.map((item, index) => (
          <Tab
            value={item.label}
            key={index}
            label={t(`tab_label_${item.label}`)}
            className={cx('tabItem', { active: tabKey === item.label })}
          />
        ))}
      </BaseTabList>

      <Box component={'div'} className={cx('body')}>
        <Box component={'div'} className={cx('tabContent')}>
          {renderTabContent()}
        </Box>
      </Box>
    </Box>
  );
};

export default UserDetail;
